// useTickets — guarda tickets comprados en localStorage (offline-first) + opcional backup en backend
import { useEffect, useState } from "react";
import axios from "axios";
import { TRIP } from "../data/trip";

const STORAGE_KEY = "viaje2026_tickets_v1";
const API = process.env.REACT_APP_BACKEND_URL + "/api";

function loadLocal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveLocal(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {}
}

// Si un ticket es maestro (tiene bundle), devuelve la lista de place_ids incluidos.
function getBundleMembers(placeId) {
  const t = (TRIP.tickets || {})[placeId];
  return Array.isArray(t?.bundle) ? t.bundle : [];
}

export function useTickets() {
  const [tickets, setTickets] = useState(loadLocal);

  useEffect(() => {
    // Sync with backend: server is the source of truth (it holds seeded files
    // like Igal's reservations + the user's uploads). We MERGE files between
    // local and server so neither side loses anything, but server data wins
    // for everything else. Files are deduped by CONTENT (first 500 chars of
    // the data URI) — labels can differ but if the underlying bytes match,
    // we treat them as the same file.
    axios.get(`${API}/tickets`).then((r) => {
      if (!Array.isArray(r.data)) return;
      const local = loadLocal();
      const merged = { ...local };
      const sigOf = (f) => {
        const d = f?.data || f?.data_b64 || f?.src || "";
        // First 500 chars is enough to differentiate even files sharing PDF
        // / PNG / JPEG headers; base64 body diverges quickly.
        return d.slice(0, 500);
      };
      r.data.forEach((t) => {
        const localT = local[t.place_id];
        if (!localT) {
          merged[t.place_id] = t;
          return;
        }
        const serverFiles = Array.isArray(t.files) ? t.files : [];
        const localFiles = Array.isArray(localT.files) ? localT.files : [];
        const seen = new Set();
        const unionFiles = [];
        // Also include legacy single-file fields in case they aren't yet in files[].
        const legacyServer = [];
        if (t.pdf_b64 && !serverFiles.some((f) => (f?.data || "") === t.pdf_b64)) {
          legacyServer.push({ type: "pdf", data: t.pdf_b64, label: "Mi ticket" });
        }
        if (t.image_b64 && !serverFiles.some((f) => (f?.data || "") === t.image_b64)) {
          legacyServer.push({ type: "image", data: t.image_b64, label: "Mi ticket" });
        }
        for (const f of [...localFiles, ...serverFiles, ...legacyServer]) {
          if (!f) continue;
          const key = sigOf(f);
          if (!key) continue;
          if (seen.has(key)) continue;
          seen.add(key);
          unionFiles.push(f);
        }
        merged[t.place_id] = { ...localT, ...t, files: unionFiles };
      });
      setTickets(merged);
      saveLocal(merged);
    }).catch(() => {});
  }, []);

  const markPurchased = (placeId, payload = {}) => {
    const masterTicket = {
      place_id: placeId,
      purchased: true,
      note: payload.note || "",
      // Mantenemos compatibilidad: si el payload trae files[], lo usamos; si no, derivamos del legacy
      files: Array.isArray(payload.files) ? payload.files : (
        payload.pdf_b64 ? [{ data: payload.pdf_b64, label: "" }]
        : payload.image_b64 ? [{ data: payload.image_b64, label: "" }]
        : []
      ),
      image_b64: payload.image_b64 || null,
      pdf_b64: payload.pdf_b64 || null,
      purchased_at: new Date().toISOString(),
    };
    const next = { ...tickets, [placeId]: masterTicket };

    // Si tiene bundle, propaga: marca cada incluido como cubierto, vinculado al maestro.
    const bundleMembers = getBundleMembers(placeId);
    bundleMembers.forEach((memberId) => {
      next[memberId] = {
        place_id: memberId,
        purchased: true,
        note: `Incluido en el ticket maestro: ${placeId}`,
        image_b64: null,
        pdf_b64: null,
        files: [],
        included_in: placeId,
        purchased_at: new Date().toISOString(),
      };
    });

    setTickets(next);
    saveLocal(next);
    // Persist al backend en paralelo
    axios.post(`${API}/tickets`, masterTicket).catch(() => {});
    bundleMembers.forEach((memberId) => {
      axios.post(`${API}/tickets`, next[memberId]).catch(() => {});
    });
  };

  const remove = (placeId) => {
    const next = { ...tickets };
    delete next[placeId];

    // Si era maestro, quita también los incluidos (los compraste juntos, se van juntos).
    const bundleMembers = getBundleMembers(placeId);
    bundleMembers.forEach((memberId) => {
      if (next[memberId]?.included_in === placeId) {
        delete next[memberId];
        axios.delete(`${API}/tickets/${memberId}`).catch(() => {});
      }
    });

    setTickets(next);
    saveLocal(next);
    axios.delete(`${API}/tickets/${placeId}`).catch(() => {});
  };

  return { tickets, markPurchased, remove, hasTicket: (id) => !!tickets[id] };
}
